import { useContext, useEffect } from 'react';
import { Control, Controller, FieldValues, RegisterOptions } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import { useDispatch, useSelector } from 'react-redux';
import { IItemWithString } from 'lib/type/common';
import { emailDomainsForContactInfoAutocomplete } from 'lib/utils/contactInfoEmailDomains';
import { InputLabel } from '../shared/FormElements/FormInput/Input';
import ContactInfoEmailAutocomplete from './ContactInfoEmailAutocomplete';
import { ContactInfoFormContext } from './ContactInfoContextProvider';
import { SitecoreGenericFieldInterface, TextField } from './ContactInfo.types';
import { RootState } from '../../store/store';
import { updatePrepopulateConfig } from '../../features/contactInfoSlice';

type ContactInfoEmailFormInputProps = {
  formElements: SitecoreGenericFieldInterface;
  control: Control<FieldValues>;
  errors: Record<string, unknown>;
  rules: RegisterOptions<FieldValues>;
  trigger: (name?: string | string[]) => Promise<boolean>;
  maxLength?: number | string;
  placeholder?: string;
  toolTip?: string;
  isReadOnly?: TextField;
  prePopulateFromScreener?: TextField;
};

// Domains from ContactInfoFormContext for autocomplete; rules from buildContactInfoEmailRules.
const ContactInfoEmailFormInput = ({
  formElements,
  control,
  errors,
  rules,
  trigger,
  maxLength,
  placeholder,
  toolTip,
  isReadOnly,
  prePopulateFromScreener,
}: ContactInfoEmailFormInputProps): JSX.Element => {
  const dispatch = useDispatch();
  const emailFieldName = formElements.key.value;
  const isAuthenticated = useSelector((state: RootState) => state.authentication.isAuthenticated);
  const contactFormContext = useContext<any>(ContactInfoFormContext);
  const sitecoreContactEmailDomains = contactFormContext?.contactEmailDomainsList as
    | IItemWithString[]
    | undefined;
  const isReadOnlyFieldValue = isReadOnly?.value;
  const isReadOnlyForEmail =
    isReadOnlyFieldValue === true || String(isReadOnlyFieldValue).toLowerCase() === 'true';
  const isEmailDisabled = isAuthenticated && isReadOnlyForEmail;

  useEffect(() => {
    if (prePopulateFromScreener !== undefined) {
      dispatch(
        updatePrepopulateConfig({
          [emailFieldName]: {
            readOnly: isReadOnly?.value,
            prePopulate: prePopulateFromScreener?.value,
          },
        })
      );
    }
  }, []);

  return (
    <Controller
      name={emailFieldName}
      control={control}
      defaultValue=""
      rules={rules}
      render={({ field }) => {
        const hasError = !!errors[emailFieldName];
        return (
          <FormControl fullWidth>
            <InputLabel
              name={emailFieldName}
              label={formElements.label}
              toolTip={toolTip}
              control={control}
              errors={errors}
            />
            <ContactInfoEmailAutocomplete
              name={emailFieldName}
              hasError={hasError}
              emailDomains={emailDomainsForContactInfoAutocomplete(sitecoreContactEmailDomains)}
              maxLength={maxLength}
              placeholder={placeholder}
              disabled={isEmailDisabled}
              fieldProps={{
                ...field,
                onChange: (value: string) => {
                  field.onChange(value);
                },
                onBlur: () => {
                  field.onBlur();
                  void trigger(emailFieldName);
                },
              }}
            />
            {errors && (
              <ErrorMessage
                errors={errors}
                name={emailFieldName}
                render={({ message }) => (
                  <Box
                    data-testid={`${emailFieldName}-error`}
                    sx={{ color: (theme) => theme.palette.error.main, fontSize: '0.8125rem' }}
                  >
                    {message}
                  </Box>
                )}
              />
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default ContactInfoEmailFormInput;
